import { ThemeProvider } from '@emotion/react';
import { createTheme, Stack, Typography } from '@mui/material';
import classNames from 'classnames';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import { Provider } from 'react-redux';
import Drawing from '/@/components/drawing';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit.ts';
import { formatGeometry, formatPosition, getBounds, getCenterOfGeometry } from '/@/core/fields';
import type { Geometry, Polygon } from '/@/core/fields/geometry.ts';
import settings from '/@/core/settings';
import { convertAreaFromM2 } from '/@/hooks/geometry/area.tsx';
import { _ } from '/@/locale';
import configureStore from '/@/store/index.ts';
import type { Field } from '/@/types/field';
import { isMobile, isMultiPolygon } from '/@/utils';
import { getMarkerIcon } from '/@/utils/index';
import colors from '../theme-provider/colors.tsx';
import fieldviewSVG from './assets/fieldview.svg';
import mda from './assets/mda.tsx';
import pointSVG from './assets/point.tsx';
import polygonSVG from './assets/polygon.svg';
import trialSVG from './assets/trial.svg';
import trialCornSVG from './assets/trial-corn.tsx';
import trialSoySVG from './assets/trial-soy.svg';
import DeviceLocation from './device-location';
import Geocoder from './geocoder';
import ItemOverlay from './item-overlay';
import styles from './styles.module.css';

const MapComponent = forwardRef(
    (
        {
            fields = [],
            overlay,
            country,
            offline = false,
            noGeocoder = false,
            simplifyPolygons = false,
            onClick,
            onFieldClick,
            onOverlayChange,
            trackEvent,
            getMarkerIcon: customGetMarkerIcon,
            zoomPosition = 'TOP_RIGHT',
            zoomControls = true,
            selectedField,
            radius,
            onOverlayClose,
            expand,
            isLoading = false,
            showOverlayCard = true,
            children,
            location,
        }: {
            fields: Field[];
            overlay: any;
            country: string;
            offline: boolean;
            noGeocoder: boolean;
            simplifyPolygons: boolean;
            onClick: (position: [number, number]) => void;
            onFieldClick: (field: Field) => void;
            onOverlayChange: (overlay: any) => void;
            trackEvent: (event: string) => void;
            getMarkerIcon: (field: Field) => string;
            zoomPosition: 'TOP_RIGHT' | 'BOTTOM_RIGHT';
            zoomControls: boolean;
            selectedField?: Field;
            radius: boolean;
            onOverlayClose: () => void;
            expand: () => void;
            isLoading: boolean;
            showOverlayCard: boolean;
            children: React.ReactNode;
            location?: string;
        },
        ref
    ) => {
        const [query, setQuery] = useState('');
        const [searchFocus, setSearchFocus] = useState(false);
        const [simplifyPolygonsState, setSimplifyPolygonsState] = useState(false);
        const [loading, setLoading] = useState(false);
        const [mapLoadError, setMapLoadError] = useState(false);
        const muiTheme = createTheme({ app: settings.app.theme });
        useEffect(() => {
            setLoading(isLoading);
        }, [isLoading]);
        const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
        const mapNodeRef = useRef<HTMLDivElement>(null);
        const markersRef = useRef<Record<string, google.maps.marker.AdvancedMarkerElement[]>>({});
        const polygonsRef = useRef<Record<string, google.maps.Polygon>>({});
        const overlayRef = useRef<google.maps.Data | google.maps.KmlLayer | google.maps.GroundOverlay | null>(null);
        const mapInstanceRef = useRef<google.maps.Map | null>(null);
        const googleMapsIntervalRef = useRef<NodeJS.Timeout | null>(null);
        const googleMapsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const units = useUnitProvider();
        const store = configureStore();
        const initializeMap = () => {
            try {
                if (!mapNodeRef.current || offline) {
                    return;
                }
                if (typeof window.google !== 'undefined' && typeof window.google.maps !== 'undefined') {
                    createMapInstance();
                } else {
                    setMapLoadError(true);
                }
            } catch {
                setMapLoadError(true);
            }
        };

        const createMapInstance = () => {
            try {
                if (!mapNodeRef.current || offline) {
                    return;
                }

                const options = {
                    disableDefaultUI: true,
                    zoomControl: !isMobile() && zoomControls,
                    zoomControlOptions: {
                        position: google.maps.ControlPosition[zoomPosition as keyof typeof google.maps.ControlPosition],
                    },
                    mapId: 'DEMO_MAP_ID',
                    mapTypeId: 'hybrid',
                };

                mapInstanceRef.current = new google.maps.Map(mapNodeRef.current, options);

                // Add click listener
                mapInstanceRef.current.addListener('click', (ev: google.maps.MapMouseEvent) => {
                    ev?.domEvent?.preventDefault();
                    if (onClick && ev.latLng) {
                        onClick([ev.latLng.lng(), ev.latLng.lat()]);
                    }
                });

                // Add zoom change listener for polygon simplification
                if (simplifyPolygons) {
                    mapInstanceRef.current.addListener('zoom_changed', () => {
                        const zoom = mapInstanceRef.current?.getZoom();
                        const newSimplifyPolygons = (zoom || 0) < 13;
                        setSimplifyPolygonsState(newSimplifyPolygons);
                    });
                }
            } catch (error) {
                setMapLoadError(true);
            }
        };

        useEffect(() => {
            initializeMap();

            return () => {
                // Clean up intervals and timeouts
                if (googleMapsIntervalRef.current) {
                    clearInterval(googleMapsIntervalRef.current);
                    googleMapsIntervalRef.current = null;
                }
                if (googleMapsTimeoutRef.current) {
                    clearTimeout(googleMapsTimeoutRef.current);
                    googleMapsTimeoutRef.current = null;
                }

                if (mapInstanceRef.current) {
                    google.maps.event.clearInstanceListeners(mapInstanceRef.current);
                }
            };
        }, [offline, zoomControls, zoomPosition, simplifyPolygons]);

        // Re-layout when the container size changes (common on mobile flex layouts)
        useEffect(() => {
            const node = mapNodeRef.current;
            if (!node || typeof ResizeObserver === 'undefined') return;

            const observer = new ResizeObserver(() => {
                if (!mapInstanceRef.current) return;
                google.maps.event.trigger(mapInstanceRef.current, 'resize');
            });
            observer.observe(node);
            return () => observer.disconnect();
        }, []);

        // Expose map methods via ref
        useImperativeHandle(ref, () => ({
            setCenter: (position: [number, number]) => {
                if (!mapInstanceRef.current) return;
                mapInstanceRef.current.setCenter(formatPosition(position));
                mapInstanceRef.current.setZoom(14);
            },
            setZoom: (value: number) => {
                if (mapInstanceRef.current) mapInstanceRef.current.setZoom(value);
            },
            getZoom: () => {
                try {
                    return mapInstanceRef.current ? mapInstanceRef.current.getZoom() : 0;
                } catch {
                    return 0;
                }
            },
            zoomMarker: (position: google.maps.LatLngLiteral, zoom: number) => {
                try {
                    if (!mapInstanceRef.current) return;
                    mapInstanceRef.current.setZoom(zoom);
                    mapInstanceRef.current.setCenter(position);
                } catch {
                    mapInstanceRef.current?.setZoom(4);
                }
            },
            fitPoints: (points: Geometry, maxZoom?: number, useEventListener = true) => {
                const fit = (mapInstanceRef) => {
                    try {
                        if (mapInstanceRef.current) {
                            mapInstanceRef.current.fitBounds(getBounds(points));
                            const zoom = mapInstanceRef.current.getZoom();
                            if (maxZoom && zoom !== undefined && zoom < maxZoom) {
                                mapInstanceRef.current.setZoom(maxZoom);
                            }
                        }
                    } catch {}
                };
                if (!mapInstanceRef.current) return;

                if (useEventListener) {
                    google.maps.event.addListenerOnce(mapInstanceRef.current, 'idle', () => {
                        fit(mapInstanceRef);
                    });
                } else {
                    fit(mapInstanceRef);
                }
            },
            fitBounds: (bounds: google.maps.LatLngBounds) => {
                if (!mapInstanceRef.current) return;
                mapInstanceRef.current.fitBounds(bounds, 3);
            },
            map: mapInstanceRef.current,
            getCenter: () => {
                try {
                    return mapInstanceRef.current ? mapInstanceRef.current.getCenter() : null;
                } catch {
                    return null;
                }
            },
            focusMap: () => {
                if (!mapInstanceRef.current) return;
                if (fields.length > 0) {
                    const bounds = new google.maps.LatLngBounds();
                    fields.map(({ lat, lng }) => {
                        bounds.extend(new google.maps.LatLng(lat, lng));
                    });
                    mapInstanceRef.current.fitBounds(bounds);
                }
            },
            infoWindowRef: infoWindowRef,
        }));

        const renderOverlay = (overlayProps: any) => {
            if (overlayRef.current) {
                overlayRef.current.setMap(null);
                overlayRef.current = null;
            }

            if (!overlayProps) return;
            const version = overlayProps.version || overlayProps;

            if (!version || !mapInstanceRef.current) {
                return;
            }

            if (version.fileType === 'geojson') {
                setLoading(true);
                const errorTimeout = setTimeout(() => {
                    setLoading(false);
                }, 3000);

                overlayRef.current = new google.maps.Data({
                    map: mapInstanceRef.current,
                    style: (feature: google.maps.Data.Feature) => ({
                        fillColor: feature.getProperty('fill') as string,
                        fillOpacity: 0.5,
                        strokeWeight: 0,
                    }),
                });

                overlayRef.current.loadGeoJson(version.file, null, () => {
                    setLoading(false);
                    clearTimeout(errorTimeout);
                });
            } else if (version.fileType === 'kml') {
                overlayRef.current = new google.maps.KmlLayer({
                    map: mapInstanceRef.current,
                    url: version.file,
                    suppressInfoWindows: true,
                });
            } else if (version.fileType === 'png') {
                const imageBounds = {
                    north: version.north,
                    south: version.south,
                    east: version.east,
                    west: version.west,
                };
                overlayRef.current = new google.maps.GroundOverlay(version.file, imageBounds, {
                    map: mapInstanceRef.current,
                    opacity: 0.5,
                });
            }
        };

        const getMarkerIconType = (field: Field) => {
            try {
                if (customGetMarkerIcon) return customGetMarkerIcon(field);

                return (field as any).iconType || getMarkerIcon(field, simplifyPolygonsState);
            } catch (error) {
                return 'point';
            }
        };

        const formattedFields = useMemo(() => {
            return fields.map((field) => {
                const [lng, lat] = getCenterOfGeometry(field.geometry);

                return {
                    ...field,
                    geometry: simplifyPolygonsState ? ([[lng, lat]] as Polygon) : field.geometry,
                    iconType: getMarkerIconType(field),
                    lat,
                    lng,
                };
            });
        }, [fields, simplifyPolygonsState]);

        const renderMarkers = (fields: typeof formattedFields) => {
            try {
                if (!mapInstanceRef.current) {
                    return;
                }

                // Remove old fields
                const visibleFields = fields.map((field) => field._id);

                for (const id of Object.keys(markersRef.current)) {
                    if (!visibleFields.includes(id)) {
                        for (const marker of markersRef.current[id] || []) {
                            marker.map = null;
                        }
                        delete markersRef.current[id];
                    }
                }

                for (const id of Object.keys(polygonsRef.current)) {
                    if (!visibleFields.includes(id)) {
                        polygonsRef.current[id]?.setMap(null);
                        delete polygonsRef.current[id];
                    }
                }

                // Add / update current fields
                for (const field of fields) {
                    renderMarker(field);
                }
            } catch {}
        };

        const renderMarker = (marker: (typeof formattedFields)[number]) => {
            const geometryMarkers = isMultiPolygon(marker.geometry)
                ? marker.geometry.map((geometry, index) => ({
                      ...marker,
                      geometry: geometry,
                      _id: `${marker._id}-${index}`,
                  }))
                : [
                      {
                          ...marker,
                          geometry: marker.geometry,
                      },
                  ];

            try {
                if (!mapInstanceRef.current) {
                    return;
                }

                if (!markersRef.current[marker._id]) {
                    markersRef.current[marker._id] = [];
                }
                const markers = markersRef.current[marker._id];

                while (markers.length > 0) {
                    const removedMarker = markers.pop();
                    if (removedMarker) {
                        removedMarker.map = null;
                    }
                }

                // Add new markers
                try {
                    const iconType = marker.iconType;
                    const iconConfig = markerMapIcons[iconType as keyof typeof markerMapIcons];
                    const mkr = new google.maps.marker.AdvancedMarkerElement({
                        map: mapInstanceRef.current,
                        position: formatPosition([marker.lng, marker.lat]),
                        content: createMarkerContent(iconConfig, iconType, {
                            name: _(marker.name),
                            area: marker.area,
                        }),
                        title: _(marker.name) || '',
                    });

                    markers.push(mkr);
                    mkr.addListener('click', (ev: google.maps.MapMouseEvent) => {
                        try {
                            ev?.domEvent?.preventDefault();
                            if (onFieldClick) onFieldClick(marker);

                            const content = document.createElement('div');

                            const growerName = (marker as Field & { growerName?: string }).growerName;

                            const InfoWindowContent = () =>
                                growerName ? (
                                    <Stack
                                        className={styles.growerTooltip}
                                        style={{
                                            background: '#10384F',
                                            borderRadius: '2px',
                                            padding: '4px 8px',
                                            color: '#FFFFFF',
                                            letterSpacing: '0.25px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Typography
                                            variant='caption'
                                            style={{ fontSize: '12px', lineHeight: '16px', color: '#FFFFFF' }}
                                        >
                                            {growerName}
                                        </Typography>
                                        <Typography
                                            fontWeight='bold'
                                            variant='body2'
                                            style={{ fontSize: '14px', lineHeight: '20px', color: '#FFFFFF' }}
                                        >
                                            {_(marker.name)}
                                        </Typography>
                                    </Stack>
                                ) : (
                                    <Stack
                                        className={styles.growerTooltip}
                                        style={{
                                            background: '#10384F',
                                            borderRadius: '2px',
                                            padding: '4px 8px',
                                            color: '#FFFFFF',
                                            letterSpacing: '0.25px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Typography
                                            variant='subtitle1'
                                            fontWeight='bold'
                                            style={{ fontSize: '16px', color: '#FFFFFF' }}
                                        >
                                            {_(marker.name)}
                                        </Typography>
                                        {marker.area && (
                                            <Typography variant='body2' style={{ fontSize: '14px', color: '#FFFFFF' }}>
                                                {convertAreaFromM2(marker.area, units).toString()}
                                            </Typography>
                                        )}
                                    </Stack>
                                );

                            const root = document.createElement('div');
                            ReactDOM.render(<InfoWindowContent />, root);
                            content.appendChild(root);

                            if (infoWindowRef.current) {
                                infoWindowRef.current.close();
                            }
                            infoWindowRef.current = new google.maps.InfoWindow({
                                content,
                                headerDisabled: true,
                            });
                            infoWindowRef.current.open({
                                anchor: mkr,
                                map: mapInstanceRef.current,
                                shouldFocus: false,
                            });
                        } catch {}
                    });
                } catch {}

                geometryMarkers.forEach((marker) => {
                    // Format geometry using formatGeometry which handles all cases recursively
                    const paths = formatGeometry(marker.geometry);

                    // Draw polygon lines
                    try {
                        // Create polygon with paths from the start
                        const polygonOptions: google.maps.PolygonOptions = {
                            map: mapInstanceRef.current,
                            paths: paths,
                            strokeColor: '#FFFFFF',
                            strokeWeight: 2,
                            clickable: !!onFieldClick,
                        };

                        const polygon = polygonsRef.current[marker._id] || new google.maps.Polygon(polygonOptions);

                        if (!polygonsRef.current[marker._id]) {
                            polygon.addListener('click', (ev: google.maps.MapMouseEvent) => {
                                try {
                                    ev?.domEvent?.preventDefault();
                                    if (onFieldClick) onFieldClick(marker);
                                } catch {}
                            });
                            polygonsRef.current[marker._id] = polygon;
                        } else {
                            // Update existing polygon paths
                            polygon.setPaths(paths);
                        }

                        if (selectedField) {
                            if (marker.id === selectedField.id) {
                                polygon.setOptions({
                                    fillColor: colors.themes[settings.app?.theme]?.mui?.primary?.primary || '#1976d2',
                                    fillOpacity: 0.5,
                                });
                            } else {
                                polygon.setOptions({ fillColor: 'gray', fillOpacity: 0.3 });
                            }
                        }
                    } catch {}
                });
            } catch {}
        };

        // Helper function to create marker content for AdvancedMarkerElement
        const createMarkerContent = (
            iconConfig: {
                url: string | ((props?: { selected?: boolean }) => JSX.Element);
                anchor?: (() => google.maps.Point | undefined) | google.maps.Point;
            },
            iconType: string,
            tooltipText?: { name: string; area: number }
        ) => {
            const element = document.createElement('div');
            if (iconType !== 'polygon') {
                element.style.width = '35px';
                element.style.height = '35px';
                element.style.cursor = 'pointer';
            }

            if (typeof iconConfig.url === 'function') {
                // For point markers, render the React component
                const PointComponent = iconConfig.url;
                ReactDOM.render(
                    <Provider store={store}>
                        <ThemeProvider theme={muiTheme}>
                            {React.createElement(PointComponent, {
                                selected: iconType === 'selected',
                                ...tooltipText,
                            })}
                        </ThemeProvider>
                    </Provider>,
                    element
                );
            } else {
                // For other markers, use background image
                element.style.backgroundImage = `url(${iconConfig.url as string})`;
                element.style.backgroundSize = 'contain';
                element.style.backgroundRepeat = 'no-repeat';
                element.style.backgroundPosition = 'center';
            }

            if (iconConfig.anchor) {
                const anchor = typeof iconConfig.anchor === 'function' ? iconConfig.anchor() : iconConfig.anchor;
                if (anchor) {
                    element.style.transform = `translate(${-anchor.x}px, ${-anchor.y}px)`;
                }
            }

            return element;
        };

        // Update markers when fields change
        useEffect(() => {
            if (mapInstanceRef.current) {
                renderMarkers(formattedFields);
            }
        }, [formattedFields, selectedField]);

        // Update overlay when it changes
        useEffect(() => {
            if (mapInstanceRef.current) {
                renderOverlay(overlay);
            }
        }, [overlay]);

        const handlePositionResult = (position: any) => {
            if (!mapInstanceRef.current) return;
            mapInstanceRef.current.setCenter(formatPosition(position));
            mapInstanceRef.current.setZoom(14);
        };

        const handleSearchChange = (value: string) => {
            setQuery(value);
            if (!value) setSearchFocus(false);
        };

        const handleSearchFocus = () => setSearchFocus(true);
        const handleSearchBlur = () => setSearchFocus(false);

        const handleSearchReset = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            setQuery('');
            setSearchFocus(false);
        };

        // Fit bounds/center based on external location string
        useEffect(() => {
            try {
                if (!mapInstanceRef.current) return;
                const value = (location || '').trim();
                if (!value || value.length < 3) return;
                const geocoder = new google.maps.Geocoder();
                const options: google.maps.GeocoderRequest = { address: value };

                // Restrict geocoding to the selected country
                if (country) {
                    options.componentRestrictions = { country: country.toLowerCase() };
                }

                geocoder.geocode(options, (results, status) => {
                    try {
                        if (status !== 'OK' || !results || results.length === 0) return;
                        const geometry = results[0].geometry;
                        if (geometry.viewport) {
                            mapInstanceRef.current?.setCenter(geometry.location);
                            mapInstanceRef.current?.fitBounds(geometry.viewport);
                            return;
                        }
                        if ((geometry as any).bounds) {
                            mapInstanceRef.current?.fitBounds((geometry as any).bounds);
                            return;
                        }
                        const loc = geometry.location;
                        mapInstanceRef.current?.setCenter({ lat: loc.lat(), lng: loc.lng() });
                        mapInstanceRef.current?.setZoom(14);
                    } catch (error) {
                        console.error('Error geocoding location:', error);
                    }
                });
            } catch {}
        }, [location, country]);

        return (
            <>
                {offline && (
                    <div className={styles.offline}>
                        <Drawing
                            type='offline'
                            color='white'
                            title={_('Sin conexión')}
                            subtitle={_('Te mostraremos el mapa cuando tengas conexión a Internet.')}
                        />
                    </div>
                )}
                {mapLoadError && !offline && (
                    <div className={styles.offline}>
                        <Drawing
                            type='offline'
                            color='white'
                            title={_('Error al cargar el mapa')}
                            subtitle={_(
                                'No se pudo cargar el mapa. Por favor, revisa tu conexión o intenta más tarde.'
                            )}
                        />
                    </div>
                )}
                <div className={classNames(styles.root, radius && styles.radius)} ref={mapNodeRef} />
                {loading && (
                    <div className={styles.loadGeoJson}>
                        <Spinner />
                    </div>
                )}
                {!noGeocoder && (
                    <div className={styles.topTools}>
                        <div className={styles.flexHorizontalGrow}>
                            <Geocoder
                                query={query}
                                country={country}
                                onResult={handlePositionResult}
                                onFocus={handleSearchFocus}
                                onBlur={handleSearchBlur}
                                onChange={handleSearchChange}
                                onReset={handleSearchReset}
                                trackEvent={trackEvent}
                            />
                            {!searchFocus && !query && (
                                <DeviceLocation onResult={handlePositionResult} trackEvent={trackEvent} />
                            )}
                        </div>
                    </div>
                )}
                {showOverlayCard && (
                    <>
                        {!isMobile() && overlay && (
                            <Draggable bounds='parent' handle='.handle'>
                                <div className={styles.itemOverlay}>
                                    <ItemOverlay
                                        {...overlay}
                                        onOverlayChange={onOverlayChange}
                                        onOverlayClose={onOverlayClose}
                                    />
                                </div>
                            </Draggable>
                        )}
                        {expand && (
                            <div
                                className={styles.btn}
                                onClick={expand}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        expand();
                                    }
                                }}
                                role='button'
                                tabIndex={0}
                            >
                                <span className='material-icons'>open_in_full</span>
                            </div>
                        )}
                    </>
                )}
                {children}
            </>
        );
    }
);

MapComponent.displayName = 'Map';

export default MapComponent;

export const markerMapIcons = {
    point: {
        url: pointSVG,
    },
    selected: {
        url: pointSVG,
    },
    polygon: {
        url: polygonSVG,
        anchor: () => (typeof google !== 'undefined' && google.maps ? new google.maps.Point(5, 5) : undefined),
    },
    fieldview: {
        url: fieldviewSVG,
    },
    trial: {
        url: trialSVG,
    },
    trialSoy: {
        url: trialSoySVG,
    },
    trialCorn: {
        url: trialCornSVG,
    },
    mda: {
        url: mda,
    },
} as const;
